import { handleErrorServer, type ServerError } from "@/lib/api/errors/ErrorHandler";

// Safe metadata for client-side console logging. The raw axios / fetch error
// carries response.data (Stripe IDs, presigned URLs, server stack traces) and
// config.url (presigned URLs in query string). Pulling just status + name keeps
// debug breadcrumbs without leaking secrets onto the shared browser console.
export function redactErrorMeta(error: unknown): {
  name?: string;
  status?: number;
  code?: string;
} {
  if (!error || typeof error !== "object") {
    return { name: typeof error };
  }
  const e = error as {
    name?: unknown;
    code?: unknown;
    status?: unknown;
    response?: { status?: unknown };
  };
  const meta: { name?: string; status?: number; code?: string } = {};
  if (typeof e.name === "string") meta.name = e.name;
  if (typeof e.code === "string") meta.code = e.code;
  // `.response` on a raw axios error, `.status` once handleError has re-wrapped
  // it and dropped the response. Both are just the number.
  const status = e.response?.status ?? e.status;
  if (typeof status === "number") meta.status = status;
  return meta;
}

/**
 * Our backend error codes are camelCase identifiers — `validationError`,
 * `couponNotApplicable`, `eventPlanNotAboveCompanyPlan`. Anything else that
 * reaches `.message` came from a backend response that carried no `errorCode`,
 * in which case `rethrowApi` falls back to the raw `data.message`: third-party
 * text such as Stripe's `card_declined`, or a whole English sentence. Those are
 * exactly what the console redaction exists to keep out.
 */
const I18N_KEY = /^[a-z][A-Za-z0-9]*$/;

/**
 * Logs a failed API call together with the backend error code, then produces
 * the ServerError the caller returns.
 *
 * Why not simply add `message` to redactErrorMeta: by the time a handler
 * catches, the client layer's `handleError` has re-wrapped the error, dropping
 * `.response` and leaving `.message` holding whatever `rethrowApi` put there —
 * the backend `errorCode` when there was one, the raw backend text when there
 * was not. Passing that through unfiltered puts `card_declined` on a shared
 * browser console.
 *
 * The pattern gate is what makes it safe: only an identifier-shaped code is
 * logged, and that code is the same i18n key the user is about to be shown, so
 * it exposes nothing new. It turns `{name:'Error'}` — all the console offered
 * while an archived Stripe price broke every purchase for 22 days — into
 * something diagnosable.
 */
export async function logApiFailure(label: string, error: unknown): Promise<ServerError> {
  const serverError = await handleErrorServer(error);
  const meta = redactErrorMeta(error);
  console.error(
    label,
    I18N_KEY.test(serverError.message) ? { ...meta, code: serverError.message } : meta,
  );
  return serverError;
}
